export class Queue<T = any> {
    private elements: T[] = [];

    /**
     * Add an element to the end of the queue
     * @param element The element to add
     */
    enqueue(element: T): void {
        this.elements.push(element);
    }

    /**
     * Get the current length of the queue
     * @returns The number of elements in the queue
     */
    getLength(): number {
        return this.elements.length;
    }

    /**
     * Remove and return the first element from the queue
     * @returns The first element or undefined if queue is empty
     */
    dequeue(): T | undefined {
        return this.elements.shift();
    }

    /**
     * Check if the queue is empty
     * @returns True if the queue is empty, false otherwise
     */
    isEmpty(): boolean {
        return this.elements.length === 0;
    }

    /**
     * Find an element by a specific key-value pair
     * @param key The property key to search by
     * @param value The value to match
     * @returns The found element or undefined
     */
    findBy<K extends keyof T>(key: K, value: T[K]): T | undefined {
        return this.elements.find(e => e[key] === value);
    }

    /**
     * Find the index of an element by a specific key-value pair
     * @param key The property key to search by
     * @param value The value to match
     * @returns The index of the found element or -1 if not found
     */
    findIndexBy<K extends keyof T>(key: K, value: T[K]): number {
        return this.elements.findIndex(e => e[key] === value);
    }

    /**
     * Get the element at the front of the queue without removing it
     * @returns The first element or undefined if queue is empty
     */
    next(): T | undefined {
        return !this.isEmpty() ? this.elements[0] : undefined;
    }

    /**
     * Clear all elements from the queue
     */
    reset(): void {
        this.elements = [];
    }

    /**
     * Get all elements in the queue
     * @returns A copy of the elements array
     */
    getAll(): T[] {
        return [...this.elements];
    }

    /**
     * Check if an element exists in the queue
     * @param element The element to check for
     * @returns True if the element exists, false otherwise
     */
    contains(element: T): boolean {
        return this.elements.includes(element);
    }
}